import React, {Fragment, useEffect} from 'react';

import {useSelector} from 'react-redux';

import {fetchNotificationsList} from 'app/notifly/actions';
import {getNotificationsByContentType} from 'app/notifly/utils';
import {
  runWhenAnyModelMatch,
  useModelChangeSubscription,
} from 'app/subscriptions';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {whenVisible} from 'app/utils/page-visibility';
import {useAsyncActions} from 'app/utils/redux';

import type {NotificationType} from 'app/notifly/types';
import type {ContentType} from 'sweet-types';

type Props = {
  contentType: ContentType;
  documentsIds: Array<string | number>;
  children: (
    notifications: Record<string, Array<NotificationType>>
  ) => Array<JSX.Element> | JSX.Element | null;
};

export const DocumentsNotificationsProvider = ({
  documentsIds,
  contentType,
  children,
}: Props) => {
  const notifications = useSelector((state) => {
    return getNotificationsByContentType(state, contentType, documentsIds);
  });

  const actions = useAsyncActions({
    fetchNotificationsList,
  });

  useEffect(() => {
    fetchNotifications();
  }, [documentsIds.join(':')]);

  const fetchNotifications = whenVisible(() => {
    if (documentsIds.length) {
      fetchNotificationsForDocument(documentsIds);
    }
  });
  const fetchNotificationsForDocument = whenVisible(
    (id?: number | Array<number>) => {
      actions.fetchNotificationsList({
        page: '1',
        page_size: `${DEFAULT_PAGE_SIZE}`,
        is_all: 'false',
        target_object_id: Array.isArray(id) ? id.join(',') : `${id}`,
        target_content_type: contentType,
      });
    }
  );

  useModelChangeSubscription((message) => {
    if (documentsIds.length === 0) {
      return;
    }

    runWhenAnyModelMatch(message, 'notification', () => fetchNotifications());
  }, []);

  return <Fragment>{children(notifications[contentType] || {})}</Fragment>;
};
